import { cloneDeep, orderBy, uniqBy } from 'lodash-es';
import { useField, useFormikContext } from 'formik';

import {
  apiOptionToFormOption,
  getQuestionNumber,
  internalOptionTypeToAPI,
  isOptionPopulated,
} from '../../../util/questions';
import {
  ALLOCATION_CARRY_FORWARD_TYPE_OPTIONS,
  getQuestionOptions,
} from '../../../util/formOptions';
import {
  QUESTION_FEATURE,
  Question,
  QUESTION_TYPE,
} from '../../../types/domainModels';
import { QuestionFormData, QuestionFormOption } from '../../../types/forms';

import FormCheckbox from '../../common/forms/FormCheckbox';
import FormSearchSelectInput from '../../common/forms/FormSearchSelectInput';

function getOptionsToCarryForward({
  existingOptions,
  question,
}: {
  existingOptions: QuestionFormOption[];
  question: Question;
}): QuestionFormOption[] {
  const carryForwardOptionIds: number[] = [];
  const carryForwardOptions = question.options.map((option) => {
    const newOption = apiOptionToFormOption();
    const existingOption = existingOptions.find(
      (eo) => eo.title === option.title,
    );
    if (existingOption) {
      newOption.id = existingOption.id;
    }

    newOption.carryOverParentId = option.id;
    newOption.image = { data: option.dataUrl, preview: null };
    newOption.title = option.title;

    carryForwardOptionIds.push(option.id);

    return newOption;
  });

  const nonEmptyOptions: QuestionFormOption[] = [];
  const existingCarryForwardOptions: QuestionFormOption[] = [];
  existingOptions.forEach((option) => {
    if (!isOptionPopulated(option)) {
      return;
    }

    if (!option.carryOverParentId) {
      nonEmptyOptions.push(option);
    } else if (option.id && carryForwardOptionIds.includes(option.id)) {
      existingCarryForwardOptions.push(option);
    }
  });

  const uniqCarriedElements = uniqBy(
    [...existingCarryForwardOptions, ...carryForwardOptions],
    (o) => o.carryOverParentId,
  );

  return orderBy(
    [...uniqBy([...uniqCarriedElements, ...nonEmptyOptions], (o) => o.title)],
    (o) => o.carryOverParentId,
  );
}

const CarryOverAllocationTotal = ({
  question,
  questions,
}: {
  question: Question | undefined;
  questions: Question[];
}) => {
  const [{ value: questionType }] =
    useField<QuestionFormData['questionType']>('questionType');
  const [{ value: options }, , optionsHelpers] =
    useField<QuestionFormOption[]>('options');
  const [{ value: carryForward }, , carryForwardHelpers] = useField<
    QuestionFormData['features']['carryForward']
  >('features.carryForward');

  if (questionType?.value !== QUESTION_TYPE.ALLOCATION) {
    return null;
  }

  const firstActiveQuestionId = questions.find((q) => q.isActive)?.id;
  const isDisabledFirstQuestion =
    !carryForward.enabled &&
    (questions.length === 0 || firstActiveQuestionId === question?.id);

  return (
    <>
      <FormCheckbox
        checkboxLabel="Carry Forward"
        disabled={isDisabledFirstQuestion}
        name="features.carryForward.enabled"
        onChange={(value) => {
          if (!value) {
            optionsHelpers.setValue(
              options.map((option) => ({
                ...option,
                carryOverParentId: null,
              })),
            );

            carryForwardHelpers.setValue({
              enabled: false,
              matrixOption: null,
              question: null,
              type: null,
            });
          }
        }}
        tooltip={
          <div>
            <p>
              This feature allows you to insert options from a previous question
              into this question.
            </p>
            {isDisabledFirstQuestion && (
              <p className="mt-4 font-bold">
                Disabled: Cannot carry forward into the first active question.
              </p>
            )}
          </div>
        }
      />
      {carryForward.enabled && (
        <CarryOverAllocationTotalConfiguration
          options={options}
          question={question}
          questions={questions}
        />
      )}
    </>
  );
};

export default CarryOverAllocationTotal;

const CarryOverAllocationTotalConfiguration = ({
  options,
  question,
  questions,
}: {
  options: QuestionFormOption[];
  question: Question | undefined;
  questions: Question[];
}) => {
  const { setFieldValue, setValues, values } =
    useFormikContext<QuestionFormData>();

  const isCarryOverDisabled =
    values.features.carryForward.type?.value !==
    QUESTION_FEATURE.CARRY_FORWARD_SELECTED;

  const [{ value: optionType }] =
    useField<QuestionFormData['optionType']>('optionType');

  const questionId = question?.id;
  const isCurrentQuestionDemographic = question?.isDemographic;
  const questionNumber = getQuestionNumber({ question, questions });

  const carryForwardQuestionOptions = getQuestionOptions({
    filterFn: (q) => {
      const { contentTypeId, id, isDemographic, questionTypeId, sort } = q;

      const isNotCurrentQuestion = id !== questionId;
      const hasSameContentType =
        internalOptionTypeToAPI(optionType?.value) === contentTypeId;
      const hasOptionsToCarryForward =
        questionTypeId === QUESTION_TYPE.ALLOCATION;
      const isPreviousQuestion = isCurrentQuestionDemographic
        ? sort < questionNumber
        : isDemographic || sort < questionNumber;

      return (
        isNotCurrentQuestion &&
        hasSameContentType &&
        hasOptionsToCarryForward &&
        isPreviousQuestion
      );
    },
    questions,
  });

  return (
    <div>
      <div className="flex p-2 space-x-4">
        <span className="flex-shrink-0 text-xs">
          Carry
          <br />
          forward
        </span>
        <div className="w-44">
          <FormSearchSelectInput
            name="features.carryForward.type"
            onChange={() => {
              setFieldValue('allocationUseCarryOverOptionAsMax', false);
            }}
            options={ALLOCATION_CARRY_FORWARD_TYPE_OPTIONS}
          />
        </div>
        <span className="flex-shrink-0 text-xs">
          from
          <br />
          question
        </span>
        <div className="flex-grow">
          <FormSearchSelectInput
            name="features.carryForward.question"
            onChange={(selectedQuestion) => {
              if (!selectedQuestion) {
                return;
              }

              const newFormData = cloneDeep(values);

              newFormData.features.carryForward.type =
                ALLOCATION_CARRY_FORWARD_TYPE_OPTIONS.find(({ value }) => {
                  return (
                    value === newFormData.features.carryForward.type?.value
                  );
                }) ?? null;

              newFormData.features.carryForward.question = selectedQuestion;
              newFormData.options = getOptionsToCarryForward({
                existingOptions: options,
                question: selectedQuestion.value,
              });

              setValues(newFormData);
            }}
            options={carryForwardQuestionOptions}
          />
        </div>
      </div>
      <div className="flex flex-col px-2 py-1 space-y-2">
        <FormCheckbox
          checkboxLabel="Carry Over Allocation Total"
          disabled={isCarryOverDisabled}
          name="allocationUseCarryOverOptionAsMax"
        />
      </div>
    </div>
  );
};
